'use client';
import { FC, useState, useEffect } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import MobileNumberField from '@/components/Inputs/MobileNumberField';
import useToggle from '@/hooks/useToggle';
import { Text, Button, Svg } from '@/components';
import { regex } from '@/utils/helper';
import { ComponentPropTypes } from './LoginSignupFlow';
import { getOtpAction } from '@/app/actions';
import { toast } from 'react-toastify';
import { storage } from '@/config';
import SecureIcon from '@/icons/SecureIcon';
import { useCleverTap } from '@/context/clevertapContext';
import { useUser } from '@/context/userContext';

interface FormTypes {
  mobilenumber: string;
}

type PropTypes = ComponentPropTypes;

const FormSchema = yup.object().shape({
  mobilenumber: yup
    .string()
    .trim()
    .required('Phone Number is Required')
    .length(10, 'Please enter a valid 10-digit mobile number.')
    .min(10, 'Please enter a valid 10-digit mobile number')
    .max(10, 'Please enter a valid 10-digit mobile number')
    .matches(regex.mobilenumber, 'Please enter a valid mobile number'),
});

const LoginWithMobileNumber: FC<PropTypes> = ({ setIndex, source }) => {
  const { value: isSubmitting, toggleValue: setSubmitting } = useToggle();
  const { loginModalMessage, setLoginModalMessage } = useUser();
  const { pushEvent } = useCleverTap();
  const [validationError, setValidationError] = useState<string | null>(null);

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
    trigger,
    setValue,
  } = useForm<FormTypes>({
    resolver: yupResolver(FormSchema) as any,
    defaultValues: {
      mobilenumber: '',
    },
    mode: 'onChange',
  });

  const mobileNumberValue = watch('mobilenumber');

  useEffect(() => {
    const validateMobile = async () => {
      if (mobileNumberValue) {
        if (mobileNumberValue.length > 10) {
          const truncated = mobileNumberValue.slice(0, 10);
          setValue('mobilenumber', truncated);
          return;
        }

        if (!/^\d+$/.test(mobileNumberValue)) {
          setValidationError('Only numbers are allowed');
        } else if (
          mobileNumberValue.length === 10 &&
          !/^[6-9]/.test(mobileNumberValue)
        ) {
          setValidationError('Mobile number must start with 6, 7, 8, or 9');
        } else {
          setValidationError(null);
          await trigger('mobilenumber');
        }
      } else {
        setValidationError(null);
      }
    };
    validateMobile();
  }, [mobileNumberValue, trigger, setValue]);

  const onSubmit = handleSubmit(async values => {
    if (validationError) return;
    setSubmitting(true);
    const response = await getOtpAction(values);
    if (response) {
      const parsedResponse = JSON.parse(response);
      if (parsedResponse?.success) {
        storage.set('mobile_number', parsedResponse?.mobile);
        toast.success(parsedResponse?.message);
        setIndex(1);
      } else {
        toast.error(parsedResponse?.message);
      }
    } else {
      toast.error('Error occurred while generating OTP');
    }
    setSubmitting(false);
  });

  useEffect(() => {
    const data: any = {
      'Page URL': window.location.href,
      'Page Name': document.title,
      Step: 'Enter Mobile Number',
    };
    if (source) {
      data['Source'] = source;
    }
    pushEvent({
      event: 'Login Flow',
      data,
    });
    return () => {
      setLoginModalMessage('');
    };
  }, []);

  return (
    <>
      {/* <div aria-label='welcome'>
        <Heading className='mb-2 text-center text-base'>Welcome to</Heading>
        <Logo rootClasses='w-48 mx-auto' anchorClasses='pointer-events-none' />
      </div> */}
      <div className='flex flex-col gap-1'>
        <div className='flex items-center gap-1'>
          <Svg className='h-5 w-5 stroke-[#4D7225]'>
            <SecureIcon />
          </Svg>
          <Text className='text-xs font-semibold text-[#4D7225] px-0'>
            100% SECURE
          </Text>
        </div>
        {/* {message && <Text className='text-lg'>{message}</Text>} */}
        <Text className='md:text-[28px] text-2xl font-semibold text-[#38373A] text-left px-0 leading-none py-2'>
          Enter your Phone Number
        </Text>
        <Text className='text-sm opacity-50 text-left px-0'>
          {!loginModalMessage || loginModalMessage === ''
            ? 'Your phone number helps with order updates, and quick support'
            : loginModalMessage}
        </Text>
      </div>
      <form name='login-signup-form' onSubmit={onSubmit} className='space-y-6'>
        <MobileNumberField
          autoFocus={true}
          register={register}
          error={errors?.mobilenumber}
        />
        <Button
          type='submit'
          label={isSubmitting ? 'Please wait....' : 'CONTINUE'}
          disabled={
            isSubmitting ||
            !mobileNumberValue ||
            mobileNumberValue.length !== 10 ||
            !!validationError
          }
          className={
            isSubmitting ||
            !mobileNumberValue ||
            mobileNumberValue.length !== 10 ||
            !!validationError
              ? 'cursor-not-allowed bg-[#dededf] text-[#605f61] hover:bg-[#e2e2e3]'
              : ''
          }
        />
      </form>
    </>
  );
};

export default LoginWithMobileNumber;
